import {
  useChangeNotificationSettings, useNotificationSettings, useNotificationSettingsSaving, useNotificationTypes, useResetAgentNotifications,
  type SettingValue,
} from "@/shared/api/notifications";
import NotificationGrid from "@/shared/components/NotificationGrid";
import { GhostButton, Pane } from "@/shared/components/settings-atoms";
import { Segmented } from "@/shared/components/teams-ui";
import { openWorkspaceSettings } from "@/shared/lib/openWorkspaceSettings";

import type { Agent } from "@/shared/types";

function options(mainEnabled: boolean | undefined): { value: SettingValue; label: string }[] {
  const follow = mainEnabled === undefined ? "Follow" : `Follow (${mainEnabled ? "on" : "off"})`;
  return [{ value: "follow", label: follow }, { value: "on", label: "On" }, { value: "off", label: "Off" }];
}

/* Notifications — the signed-in person's own choice, for this agent, per notice and channel.
   Follow means the main settings decide. Each pick saves at once. */
export default function NotificationsSection({ agent }: { agent: Agent }) {
  const types = useNotificationTypes();
  const settings = useNotificationSettings(agent.id);
  const main = useNotificationSettings(null);
  const change = useChangeNotificationSettings(agent.id);
  const reset = useResetAgentNotifications(agent.id);
  const saving = useNotificationSettingsSaving();
  const allFollow = settings.data?.settings.every((c) => c.value === "follow") ?? true;

  const desc = (
    <>
      Which notices about this agent reach you. Only you see these. Follow uses your{" "}
      <a
        href="#/workspace-settings/notifications"
        onClick={(e) => { e.preventDefault(); openWorkspaceSettings("notifications"); }}
        className="underline underline-offset-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
      >
        main settings
      </a>.
    </>
  );

  return (
    <Pane title="Notifications" desc={desc}>
      <NotificationGrid
        types={types}
        settings={settings}
        disabled={saving}
        extra={main}
        include={(type) => type.per_agent}
        emptyText="No notices can be set for one agent."
        footer={<GhostButton disabled={allFollow || saving} onClick={() => reset.mutate()}>Reset to my main settings</GhostButton>}
        renderCell={(cell, name) => (
          <div role="group" aria-label={name}>
            <Segmented
              value={cell.value}
              options={options(main.data?.settings.find((c) => c.type === cell.type && c.channel === cell.channel)?.enabled)}
              onChange={(value) => value !== cell.value && change.mutate([{ type: cell.type, channel: cell.channel, value }])}
            />
          </div>
        )}
      />
    </Pane>
  );
}
